import { TextField } from "@mui/material";
import * as React from "react";

import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";

import styles from "./EditableCollectionName.module.css";

interface EditableCollectionNameProps {
  name: string;
  collectionId: string;
  orgId: string;
}

export const EditableCollectionName: React.FC<EditableCollectionNameProps> = ({
  name,
  collectionId,
  orgId,
}) => {
  const [editable, setEditable] = React.useState(false);
  const [currentName, setCurrentName] = React.useState(name);
  const savingRef = React.useRef(false);
  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    setCurrentName(name);
  }, [name, collectionId]);

  const { mutateAsync: updateCollection } = useUpdateCollection();

  const save = async (value: string) => {
    if (savingRef.current || cancelledRef.current) {
      return;
    }
    savingRef.current = true;
    const trimmed = value.trim();
    try {
      await updateCollection({
        collectionId,
        resourceOwnerId: orgId,
        updates: { name: trimmed || undefined },
      });
      setCurrentName(trimmed);
      setEditable(false);
    } catch {
      // stay in edit mode — error surfaced via mutation.error
    } finally {
      savingRef.current = false;
    }
  };

  if (editable) {
    return (
      <TextField
        size="small"
        autoFocus
        defaultValue={currentName}
        placeholder="Collection name"
        onBlur={(e) => void save(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            void save((e.target as HTMLInputElement).value);
          } else if (e.key === "Escape") {
            cancelledRef.current = true;
            setEditable(false);
          }
        }}
        inputProps={{ style: { fontSize: "1.25rem", fontWeight: 700 } }}
        style={{ width: 320 }}
      />
    );
  }

  return (
    <span
      className={styles.nameDisplay}
      onClick={() => {
        cancelledRef.current = false;
        setEditable(true);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          cancelledRef.current = false;
          setEditable(true);
        }
      }}
    >
      {currentName || <i>No Name</i>}
    </span>
  );
};
